import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import {
  TextB, TextItalic, TextUnderline, TextHOne, TextHTwo,
  ListBullets, ListNumbers, LinkSimple, Eraser,
} from "@phosphor-icons/react";

const TOOLS = [
  { cmd: "bold", icon: TextB, title: "Bold" },
  { cmd: "italic", icon: TextItalic, title: "Italic" },
  { cmd: "underline", icon: TextUnderline, title: "Underline" },
  { cmd: "formatBlock:H2", icon: TextHOne, title: "Heading" },
  { cmd: "formatBlock:H3", icon: TextHTwo, title: "Subheading" },
  { cmd: "insertUnorderedList", icon: ListBullets, title: "Bulleted list" },
  { cmd: "insertOrderedList", icon: ListNumbers, title: "Numbered list" },
  { cmd: "createLink", icon: LinkSimple, title: "Insert link" },
  { cmd: "removeFormat", icon: Eraser, title: "Clear formatting" },
];

/** Lightweight rich-text editor (contentEditable). Stores HTML. */
export default function RichText({ value, onChange, testid = "rich-text" }) {
  const ref = useRef(null);

  // Sync external value in only when the editor is not focused (prevents cursor jumps).
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== (value || "")) {
      el.innerHTML = DOMPurify.sanitize(value || "", { USE_PROFILES: { html: true } });
    }
  }, [value]);

  const emit = () => onChange(ref.current?.innerHTML || "");

  const run = (cmd) => {
    ref.current?.focus();
    if (cmd.startsWith("formatBlock:")) {
      document.execCommand("formatBlock", false, cmd.split(":")[1]);
    } else if (cmd === "createLink") {
      const url = window.prompt("Link URL");
      if (url) document.execCommand("createLink", false, url);
    } else {
      document.execCommand(cmd, false, null);
    }
    emit();
  };

  return (
    <div className="overflow-hidden rounded-sm border border-neutral-300 bg-white focus-within:border-[#0055FF]">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-neutral-200 bg-neutral-50 px-1.5 py-1">
        {TOOLS.map((t) => (
          <button
            key={t.cmd}
            type="button"
            title={t.title}
            data-testid={`${testid}-${t.cmd.replace(":", "-")}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => run(t.cmd)}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-neutral-600 hover:bg-neutral-200 hover:text-[#0A0A0A]"
          >
            <t.icon size={16} weight="bold" />
          </button>
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-testid={testid}
        className="cover-body min-h-[180px] px-4 py-3 text-base leading-relaxed text-neutral-700 outline-none"
      />
    </div>
  );
}
